import React, { useCallback, useRef } from 'react';

export const usePreserveScrollOnResize = <T extends HTMLElement>(
  // fraction (0-1) of the board's total height where the selected ring is
  // centered — expressed as a fraction rather than an absolute pixel
  // position because the board's SVG is scaled to fit the container width
  // (its viewBox units don't map 1:1 to the container's CSS pixels), so the
  // fraction must be multiplied by the *current* scrollHeight at resize time.
  selectedRingCenterFraction: number | null,
) => {
  const scrollRatioRef = useRef(0);
  const lastScrollHeightRef = useRef(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const selectedRingCenterFractionRef = useRef(selectedRingCenterFraction);
  selectedRingCenterFractionRef.current = selectedRingCenterFraction;

  const onScroll = (event: React.UIEvent<T>) => {
    const elt = event.currentTarget;
    if (elt.scrollHeight <= elt.clientHeight) {
      return;
    }
    if (elt.scrollHeight !== lastScrollHeightRef.current) {
      // The browser clamped scrollTop because of an in-flight resize,
      // this scroll event doesn't reflect genuine user intent.
      return;
    }
    scrollRatioRef.current = elt.scrollTop / elt.scrollHeight;
  };

  const containerRef = useCallback((elt: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!elt) {
      return;
    }

    lastScrollHeightRef.current = elt.scrollHeight;

    const observer = new ResizeObserver(() => {
      const targetFraction = selectedRingCenterFractionRef.current;
      // The browser clamps scrollTop into [0, scrollHeight - clientHeight] automatically.
      elt.scrollTop =
        targetFraction !== null
          ? targetFraction * elt.scrollHeight - elt.clientHeight / 2
          : scrollRatioRef.current * elt.scrollHeight;
      lastScrollHeightRef.current = elt.scrollHeight;
    });
    observer.observe(elt);
    observerRef.current = observer;
  }, []);

  return { containerRef, onScroll };
};
